import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithClient } from "@/redux/baseQuery";
import {
  ClientScopeArg,
  GetClientPoisArgs,
  GetClientPoisResult,
  ListPoisRawResponse,
  PointsOfInterest,
  RemoveClientPoiArgs,
  SaveClientPoiArgs,
} from "./types";

const toResult = (response: ListPoisRawResponse): GetClientPoisResult => ({
  items: response?._embedded?.items ?? [],
  totalItems: response?.total_items ?? 0,
  pageCount: response?.page_count ?? 1,
  page: response?.page ?? 1,
});

export const clientPoiApi = createApi({
  reducerPath: "clientPoiApi",
  baseQuery: baseQueryWithClient("location"),
  tagTypes: ["ClientPOI"],
  endpoints: (builder) => ({
    getClientPois: builder.query<GetClientPoisResult, GetClientPoisArgs>({
      query: ({ page, pageSize, sort, order, accountId }) => ({
        url: "/poi",
        accountId,
        params: { page, page_size: pageSize, ...(sort ? { sort, order: order ?? "asc" } : {}) },
      }),
      transformResponse: toResult,
      providesTags: ["ClientPOI"],
    }),
    listClientPois: builder.query<PointsOfInterest[], ClientScopeArg | void>({
      query: (arg) => ({ url: "/poi", accountId: arg?.accountId, params: { all: true } }),
      transformResponse: (r: ListPoisRawResponse) => r?._embedded?.items ?? [],
      providesTags: ["ClientPOI"],
    }),
    // Derived from booking history, not from favourites — intentionally not tagged
    // with "ClientPOI" so add/remove favourite mutations don't refetch it (matches mobile).
    listClientRecentPois: builder.query<PointsOfInterest[], ClientScopeArg | void>({
      query: (arg) => ({ url: "/recent", accountId: arg?.accountId, params: { all: true } }),
      transformResponse: (r: ListPoisRawResponse) => r?._embedded?.items ?? [],
    }),
    addClientPoi: builder.mutation<PointsOfInterest, SaveClientPoiArgs>({
      // lat/lng sent as fixed-precision strings — the backend's expected format (matches mobile poiApi).
      query: ({ accountId, ...body }) => ({
        url: "/poi",
        method: "POST",
        accountId,
        body: { ...body, lat: Number(body.lat).toFixed(9), lng: Number(body.lng).toFixed(9) },
      }),
      invalidatesTags: ["ClientPOI"],
    }),
    updateClientPoi: builder.mutation<PointsOfInterest, SaveClientPoiArgs & { uuid: string }>({
      query: ({ uuid, accountId, ...body }) => ({
        url: `/poi/${uuid}`,
        method: "PUT",
        accountId,
        body: { ...body, uuid, lat: Number(body.lat).toFixed(9), lng: Number(body.lng).toFixed(9) },
      }),
      invalidatesTags: ["ClientPOI"],
    }),
    removeClientPoi: builder.mutation<void, RemoveClientPoiArgs>({
      query: ({ poiId, accountId }) => ({ url: `/poi/${poiId}`, method: "DELETE", accountId }),
      invalidatesTags: ["ClientPOI"],
    }),
  }),
});

export const {
  useGetClientPoisQuery,
  useListClientPoisQuery,
  useListClientRecentPoisQuery,
  useAddClientPoiMutation,
  useUpdateClientPoiMutation,
  useRemoveClientPoiMutation,
} = clientPoiApi;
